import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {AppConfigService} from '../core/config/app-config.service';
import {Employee} from "../admin/models/employee.model";
import {DatePipe} from "@angular/common";
import {Vacation} from "../admin/models/vacation.model";
import {PublicHoliday} from "../admin/models/publicHoliday.model";
import {AbsenceService} from "../shared/globals/absence-service.service";

@Injectable({
    providedIn: 'root'
})
export class PersonalService {

    private readonly baseUrl;

    constructor(private httpClient: HttpClient, private readonly configService: AppConfigService,
                private readonly absenceService: AbsenceService) {
        this.baseUrl = configService.getConfig().baseUrl;
    }

    /**
     * Fetches an employees details by their gmin
     * @param gmin The gmin to get details for
     */
    public fetchEmployeeByGmin(gmin: string): Observable<Employee> {
        return this.absenceService.getRequest(this.baseUrl, '/employee/gmin/' + gmin);
    }

    /**
     * Gets an array and returns an array of only unique values from it
     * @param array The array to search for unique values in
     */
    public getUniqueValues(array: any[]): any[] {
        return array.filter((value, index, self) => self.indexOf(value) === index);
    }

    /**
     * Converts a date string to yyyy-MM-dd format
     * @param dateString The string to convert
     */
    public convertDateFormat(dateString: string): string {
        const newDateString = new DatePipe('en-US').transform(dateString, 'yyyy-MM-dd');
        return newDateString ? newDateString : '';
    }

    /**
     * Gets a string of a year from a date string
     * @param dateString The string to get the year from
     */
    public getYearOfDateString(dateString: string): string {
        const newDateString = new DatePipe('en-US').transform(dateString, 'yyyy');
        return newDateString ? newDateString : '';
    }

    /**
     * Converts a date string to format dd/MM/yyyy. The api requires this formatting
     * @param dateString The string to convert
     */
    public convertDateFormatForAPI(dateString: string): string {
        if (dateString.charAt(2) === '/') {
            return dateString;
        } else {
            const newDateString = new DatePipe('en-US').transform(dateString, 'dd/MM/yyyy');
            return newDateString ? newDateString : '';
        }
    }

    /**
     * Swaps around the date and month in a date string where a datepipe won't work
     * @param dateString The string to be changed
     */
    public swapDateAndMonth(dateString: string): string {
        return dateString.substring(3, 5) + '/' + dateString.substring(0, 2) + '/' + dateString.substring(6);
    }

    /**
     * Gets the number of pending vacation requests that an employee has in a selected year
     * @param data The employee to calculate from
     * @param year The year to be filtered to
     */
    public pendingVacationRequests(data: Employee, year: number): number {
        let counter = 0;
        data.vacation.forEach((value) => {
            if (value.requestStatus === 'PENDING' &&
                Number(this.getYearForMatCard(value.endDate)) == year) {
                counter++;
            }
        });
        return counter;
    }

    /**
     * Gets the number of pending vacation days that an employee has in a selected year
     * @param data The employee to calculate from
     * @param year The year to be filtered to
     */
    public pendingVacationDays(data: Employee, year: number): number {
        let counter = 0;
        data.vacation.forEach((value) => {
            if (value.requestStatus === 'PENDING' &&
                Number(this.getYearForMatCard(value.endDate)) == year) {
                counter = counter + value.numberOfAnnualLeaveDays;
            }
        });
        return counter;
    }

    /**
     * Submits a new vacation request
     * @param data The request to be submitted
     */
    public submitVacationRequest(data: Vacation): Observable<null> {
        return this.absenceService.postRequest(this.baseUrl, '/vacation', data);
    }

    /**
     * Updates a vacation request
     * @param data The request to be updated
     */
    public modifyVacationRequest(data: Vacation): Observable<null> {
        return this.absenceService.putRequest(this.baseUrl, '/vacation', data);
    }

    /**
     * Cancels a vacation request
     * @param data The request to be cancelled
     */
    public cancelVacationRequest(data: string): Observable<null> {
        return this.absenceService.deleteRequest(this.baseUrl, '/vacation/cancel/' + data);
    }

    /**
     * Fetches a list of public holidays for a given region
     * @param data The region to be fetched
     */
    public fetchPublicHolidayList(data: string): Observable<PublicHoliday[]> {
        return this.absenceService.getRequest(this.baseUrl, '/public-holiday/region/' + data);
    }

    /**
     * Returns a string saying Yes or No given a boolean value
     * @param bool The boolean to check
     */
    public halfDayStatus(bool: boolean): string {
        if (bool) {
            return 'Yes';
        } else {
            return 'No';
        }
    }

    /**
     * Sets the date to the 31st of December for next year based on the passed in date
     * @param date the date to be converted
     */
    public convertDateToNextYearEnd(date: Date): Date {
        date.setFullYear(date.getFullYear() + 1, 11, 31);
        return date;
    }

    /**
     * Returns a string with the year only
     * @param dateString The datestring to split
     */
    public getYear(dateString: string): string {
        return dateString.substring(6, 10);
    }

    /**
     * Returns a string with the year only
     * @param dateString The datestring to split
     */
    public getYearForMatCard(dateString: string): string {
        let returnValue = '';
        if (dateString.charAt(2) == '/') {
            returnValue = dateString.substring(6, 10);
        } else {
            returnValue = dateString.substring(0, 4);
        }
        return returnValue
    }

}
